import React from "react";
import { useTranslate } from "@refinedev/core";
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Switch,
    Divider,
    Drawer,
    Button,
    InputNumber,
    Upload,
    Image,
    DatePicker,
} from "antd";
import dayjs from "dayjs";
import type { UploadProps, FormProps } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import {
    TenantAutomationFields,
    TenantApiKeyFields,
} from "../../../components/tenant/TenantGeneralFields";

interface TenantGeneralTabProps {
    formProps: FormProps;
    onFinish: (values: any) => Promise<any> | void;
    saveButtonProps: any;
    drawerOpen: boolean;
    setDrawerOpen: (open: boolean) => void;
    textareaRows: number;
    form: any;
    logoDarkPreview: string | null;
    logoLightPreview: string | null;
    handleLogoDarkUpload: UploadProps["beforeUpload"];
    handleLogoLightUpload: UploadProps["beforeUpload"];
    handleRemoveLogoDark: () => void;
    handleRemoveLogoLight: () => void;
}

export const TenantGeneralTab: React.FC<TenantGeneralTabProps> = ({
    formProps,
    onFinish,
    saveButtonProps,
    drawerOpen,
    setDrawerOpen,
    textareaRows,
    form,
    logoDarkPreview,
    logoLightPreview,
    handleLogoDarkUpload,
    handleLogoLightUpload,
    handleRemoveLogoDark,
    handleRemoveLogoLight,
}) => {
    const translate = useTranslate();

    return (
        <Form.Provider>
            <Form {...formProps} onFinish={onFinish} layout="vertical">
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate("tenants.fields.name", "Name")}
                            name="name"
                            rules={[
                                {
                                    required: true,
                                    message: "Name is required",
                                },
                            ]}
                        >
                            <Input placeholder="Enter tenant name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate("tenants.fields.slug", "Slug")}
                            name="slug"
                            rules={[
                                {
                                    required: true,
                                    message: "Slug is required",
                                },
                                {
                                    pattern: /^[a-z0-9-]+$/,
                                    message:
                                        "Slug must be lowercase alphanumeric with hyphens",
                                },
                            ]}
                        >
                            <Input placeholder="Enter tenant slug (e.g., acme-corp)" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate("tenants.fields.status", "Status")}
                            name="status"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select
                                options={[
                                    {
                                        label: translate(
                                            "tenants.fields.statusValues.active",
                                            "Active",
                                        ),
                                        value: "active",
                                    },
                                    {
                                        label: translate(
                                            "tenants.fields.statusValues.suspended",
                                            "Suspended",
                                        ),
                                        value: "suspended",
                                    },
                                    {
                                        label: translate(
                                            "tenants.fields.statusValues.trial",
                                            "Trial",
                                        ),
                                        value: "trial",
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.subscriptionStatus",
                                "Subscription",
                            )}
                            name="subscriptionStatus"
                        >
                            <Select
                                options={[
                                    {
                                        label: translate(
                                            "tenants.fields.subscriptionStatusValues.free",
                                            "Free",
                                        ),
                                        value: "free",
                                    },
                                    {
                                        label: translate(
                                            "tenants.fields.subscriptionStatusValues.trial",
                                            "Trial",
                                        ),
                                        value: "trial",
                                    },
                                    {
                                        label: translate(
                                            "tenants.fields.subscriptionStatusValues.active",
                                            "Active",
                                        ),
                                        value: "active",
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        {/* Clearing this permanently disables trial expiry for the tenant. */}
                        <Form.Item
                            label={translate(
                                "tenants.fields.trialEndsAt",
                                "Trial ends",
                            )}
                            name="trialEndsAt"
                            getValueProps={(value) => ({
                                value: value ? dayjs(value) : undefined,
                            })}
                            getValueFromEvent={(date) =>
                                date ? date.toISOString() : null
                            }
                        >
                            <DatePicker
                                format="DD/MM/YYYY"
                                style={{ width: "100%" }}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate("tenants.sections.branding", "Branding")}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.logoDark",
                                "Logo (Dark Mode)",
                            )}
                            tooltip={translate(
                                "tenants.fields.logoDarkTooltip",
                                "Logo displayed on dark backgrounds",
                            )}
                        >
                            <div className="flex flex-col gap-4">
                                {logoDarkPreview && (
                                    <div className="flex items-center gap-4">
                                        <div className="p-2 bg-gray-800 rounded">
                                            <Image
                                                src={logoDarkPreview}
                                                alt="Logo dark preview"
                                                style={{
                                                    maxHeight: 80,
                                                    maxWidth: 160,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </div>
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleRemoveLogoDark}
                                        >
                                            {translate("buttons.delete", "Delete")}
                                        </Button>
                                    </div>
                                )}
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={handleLogoDarkUpload}
                                    maxCount={1}
                                >
                                    <Button icon={<UploadOutlined />}>
                                        {translate("tenants.fields.uploadLogo", "Upload Logo")}
                                    </Button>
                                </Upload>
                            </div>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.logoLight",
                                "Logo (Light Mode)",
                            )}
                            tooltip={translate(
                                "tenants.fields.logoLightTooltip",
                                "Logo displayed on light backgrounds",
                            )}
                        >
                            <div className="flex flex-col gap-4">
                                {logoLightPreview && (
                                    <div className="flex items-center gap-4">
                                        <div className="p-2 bg-gray-100 rounded border">
                                            <Image
                                                src={logoLightPreview}
                                                alt="Logo light preview"
                                                style={{
                                                    maxHeight: 80,
                                                    maxWidth: 160,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </div>
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleRemoveLogoLight}
                                        >
                                            {translate("buttons.delete", "Delete")}
                                        </Button>
                                    </div>
                                )}
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={handleLogoLightUpload}
                                    maxCount={1}
                                >
                                    <Button icon={<UploadOutlined />}>
                                        {translate("tenants.fields.uploadLogo", "Upload Logo")}
                                    </Button>
                                </Upload>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate("tenants.sections.creditSettings", "Credit Settings")}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.pricePerCredit",
                                "Price Per Credit (€)",
                            )}
                            name="pricePerCredit"
                            tooltip={translate(
                                "tenants.fields.pricePerCreditTooltip",
                                "Price per credit in euros",
                            )}
                            rules={[
                                {
                                    type: "number",
                                    min: 0,
                                    message: translate(
                                        "tenants.fields.errors.pricePerCreditInvalid",
                                        "Price must be a positive number",
                                    ),
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder="10"
                                min={0}
                                step={0.01}
                                precision={2}
                                style={{ width: "100%" }}
                                suffix="€"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24}>
                        <Button
                            type="primary"
                            onClick={() => setDrawerOpen(true)}
                            className="mb-4"
                        >
                            {translate("tenants.fields.settings", "Open Settings")}
                        </Button>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate(
                        "tenants.sections.fileProcessing",
                        "File Processing Settings",
                    )}
                </Divider>

                <TenantAutomationFields variant="compact" />
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.localApiEndpoint",
                                "Local API Endpoint",
                            )}
                            name="localApiEndpoint"
                            rules={[
                                {
                                    type: "url",
                                    message: "Please enter a valid URL",
                                },
                            ]}
                        >
                            <Input placeholder="https://api.example.com" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    {translate("tenants.sections.apiKeys", "API Keys & Credentials")}
                </Divider>

                <TenantApiKeyFields variant="compact" />

                <Divider orientation="left">
                    {translate("tenants.sections.alientech", "Alientech Credentials")}
                </Divider>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.alientechLogin",
                                "Alientech Login",
                            )}
                            name="alientechLogin"
                            tooltip="Alientech account login"
                        >
                            <Input placeholder="Enter Alientech login" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label={translate(
                                "tenants.fields.alientechPassword",
                                "Alientech Password",
                            )}
                            name="alientechPassword"
                            tooltip="Alientech account password"
                        >
                            <Input.Password placeholder="Enter Alientech password" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                    <Col xs={24}>
                        <Button type="primary" {...saveButtonProps}>
                            {translate("buttons.save", "Save")}
                        </Button>
                    </Col>
                </Row>
            </Form>

            <Drawer
                title={translate("tenants.fields.settings", "Settings (JSON)")}
                placement="right"
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                width={600}
                styles={{
                    body: {
                        padding: "24px",
                    },
                }}
            >
                <Form form={form} layout="vertical" component={false}>
                    <Form.Item
                        name="settings"
                        tooltip="Optional JSON object for tenant-specific settings"
                        style={{ marginBottom: 0 }}
                    >
                        <Input.TextArea
                            placeholder='{"theme": "dark", "timezone": "Europe/Paris"}'
                            style={{ resize: "none" }}
                            rows={textareaRows}
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </Form.Provider>
    );
};

